import { LocalizationText } from "@/app/index";
import { Alert, Button, Flex, Tag, Text, Title } from "@/base/components/index";
import variableColors, {
  ColorFigmaNeutralGrayscaleGray70,
} from "@/base/global/variables/figma.colors.json";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
} from "@/base/static/index";
import { SectionPage } from "@/components/section-page/index";
import { forwardRef, useImperativeHandle, useState } from "react";
import { useContextNotifications } from "../context/index";
import { getAlertType } from "../context/notifications-hooks";
import { NotificationsProps, NotificationsRef } from "../types/index";
import { useNotificationsStyles } from "./notifications.styles";

export const Notifications = forwardRef<
  NotificationsRef,
  Pick<NotificationsProps, "className">
>((props, ref) => {
  const { styles } = useNotificationsStyles();
  const {
    announcements,
    isLoading,
    currentIndex,
    goNext,
    goPrev,
    dismiss,
    total,
  } = useContextNotifications();
  const [visible, setVisible] = useState(true);

  useImperativeHandle(ref, () => ({
    open: () => setVisible(true),
    close: () => setVisible(false),
  }));

  if (
    !visible ||
    (!announcements.length && !isLoading) ||
    !announcements[currentIndex]
  )
    return null;

  const current = announcements[currentIndex];

  return (
    <SectionPage>
      <Flex
        className={`${styles.root}${props.className ? ` ${props.className}` : ""}`}
        align="stretch"
      >
        {total > 1 && (
          <Flex align="flex-start" justify="center" className={styles.nav}>
            <Flex vertical gap={2} className={styles.navContent}>
              <Flex gap={8} align="flex-start">
                <Title level={4}>{currentIndex + 1}</Title>
                <Tag
                  className={styles.tag}
                  color={variableColors["ColorFigmaComplementaryBlue20"]}
                  closable={false}
                >
                  {` / ${total}`}
                </Tag>
              </Flex>

              <Text size="small">
                <LocalizationText value="module.notifications.label" />
              </Text>
            </Flex>
            <Flex gap={4} className={styles.navActions}>
              <Button
                size="small"
                variant="text"
                color="default"
                icon={<ChevronLeftIcon />}
                onClick={goPrev}
                disabled={currentIndex === 0}
              />
              <Button
                size="small"
                variant="text"
                color="default"
                icon={<ChevronRightIcon />}
                onClick={goNext}
                disabled={currentIndex === total - 1}
              />
            </Flex>
          </Flex>
        )}
        <Alert
          key={current?.Id}
          showIcon
          style={{ flex: 1 }}
          type={getAlertType(current?.TypeCode)}
          message={current?.Title}
          description={current?.Description}
          closeIcon={
            <CloseIcon color={ColorFigmaNeutralGrayscaleGray70} size={20} />
          }
          onClose={() => current && dismiss(current.Id)}
        />
      </Flex>
    </SectionPage>
  );
});

Notifications.displayName = "Notifications";
